import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Loader2, Mail, Pencil, KeyRound } from 'lucide-react'
import {
  getInitials,
  getRoleLabel,
  isManagerRole,
  roleBadgeVariant,
  type UserItem,
} from '@/components/Configuracao/usersTabConstants'
import { getUserSetorLabel } from '@/components/Configuracao/userSetorConstants'
import { canEnableEventModeForRole } from '@/utils/userEventMode'
import {
  passwordSetupBadgeVariant,
  passwordSetupLabel,
  passwordSetupNeedsResend,
} from '@/components/Configuracao/userPasswordSetupUi'

type UsersTabRowProps = {
  user: UserItem
  /** Só Admin vê/edita a coluna Valores fin. */
  showContractFinanceToggle?: boolean
  /** Desativar / senha / convite — somente ADMIN. */
  canManageLifecycle?: boolean
  resendingInviteId: string | null
  financeTogglingId: string | null
  onEdit: (user: UserItem) => void
  onResetPassword: (userId: string) => void
  onResendSetup: (user: UserItem) => void
  onToggleActive: (userId: string) => void
  onToggleContractFinance: (user: UserItem) => void
}

export function UsersTabRow({
  user,
  showContractFinanceToggle = false,
  canManageLifecycle = false,
  resendingInviteId,
  financeTogglingId,
  onEdit,
  onResetPassword,
  onResendSetup,
  onToggleActive,
  onToggleContractFinance,
}: UsersTabRowProps) {
  const needsSetup = passwordSetupNeedsResend(user.passwordSetupStatus)
  const isManager = isManagerRole(user.role)

  return (
    <tr
      className={`border-b border-[var(--glass-border)] last:border-0 transition-colors ${
        needsSetup ? 'bg-amber-500/[0.06] hover:bg-amber-500/[0.1]' : 'hover:bg-[var(--glass-bg)]'
      }`}
    >
      <td className='px-4 py-3'>
        <div className='flex items-center gap-2'>
          <div className='w-7 h-7 rounded-full bg-[var(--brand-primary)] flex items-center justify-center shrink-0'>
            <span className='text-white font-medium text-[11px]'>{getInitials(user.name)}</span>
          </div>
          <span className='font-medium text-[var(--text-primary)] text-[13px]'>{user.name}</span>
        </div>
      </td>
      <td className='px-4 py-3'>
        <span className='text-[var(--text-muted)] text-[13px]'>{user.email}</span>
      </td>
      <td className='px-4 py-3'>
        <Badge variant={roleBadgeVariant[user.role] ?? 'default'}>{getRoleLabel(user.role)}</Badge>
      </td>
      <td className='px-4 py-3'>
        <span className='text-[var(--text-muted)] text-[13px]'>{getUserSetorLabel(user.setor)}</span>
      </td>
      <td className='px-4 py-3'>
        <Badge variant={passwordSetupBadgeVariant(user.passwordSetupStatus)}>
          {passwordSetupLabel(user.passwordSetupStatus)}
        </Badge>
      </td>
      <td className='px-4 py-3'>
        <div className='flex flex-wrap gap-1'>
          {user.notifyNewFinanceProject && <Badge variant='info'>Projeto</Badge>}
          {user.notifyFinanceInbox && <Badge variant='success'>Fila</Badge>}
          {!user.notifyNewFinanceProject && !user.notifyFinanceInbox && (
            <span className='text-text-muted text-[12px]'>—</span>
          )}
        </div>
      </td>
      {showContractFinanceToggle && (
        <td className='px-4 py-3'>
          {isManager ? (
            <label
              className='inline-flex items-center gap-1.5 rounded-lg border border-[var(--glass-border)] px-2 py-1.5 text-[11px] text-[var(--text-secondary)]'
              title='Liberar valores financeiros de contrato (Finance / Gestores / CS)'
            >
              <input
                type='checkbox'
                checked={Boolean(user.canViewContractFinance)}
                disabled={financeTogglingId === user.id}
                onChange={() => onToggleContractFinance(user)}
              />
              Valores
            </label>
          ) : (
            <span className='text-text-muted text-[12px]'>—</span>
          )}
        </td>
      )}
      <td className='px-4 py-3'>
        <div className='flex flex-wrap items-center gap-1'>
          <Badge variant={user.active ? 'success' : 'muted'}>{user.active ? 'Ativo' : 'Inativo'}</Badge>
          {user.eventModeActive && canEnableEventModeForRole(user.role) && (
            <Badge variant='warning'>Evento</Badge>
          )}
        </div>
      </td>
      <td className='px-4 py-3'>
        <div className='flex flex-wrap items-center gap-1'>
          {canManageLifecycle ? (
            <>
              <button
                onClick={() => onEdit(user)}
                title='Editar'
                className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] transition-colors'
              >
                <Pencil size={14} className='text-text-muted' />
              </button>
              <button
                onClick={() => onResetPassword(user.id)}
                title='Redefinir Senha'
                className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] transition-colors'
              >
                <KeyRound size={14} className='text-text-muted' />
              </button>
              {needsSetup ? (
                <Button
                  variant='primary'
                  size='sm'
                  icon={
                    resendingInviteId === user.id ? (
                      <Loader2 size={13} className='animate-spin' />
                    ) : (
                      <Mail size={13} />
                    )
                  }
                  disabled={resendingInviteId === user.id}
                  onClick={() => onResendSetup(user)}
                  className='min-h-[36px]'
                >
                  Reenviar e-mail
                </Button>
              ) : (
                <button
                  onClick={() => onResendSetup(user)}
                  disabled={resendingInviteId === user.id}
                  title='Reenviar e-mail de cadastro'
                  className='w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.06] transition-colors disabled:opacity-50'
                >
                  {resendingInviteId === user.id ? (
                    <Loader2 size={14} className='animate-spin text-accent' />
                  ) : (
                    <Mail size={14} className='text-text-muted' />
                  )}
                </button>
              )}
              <Button variant='ghost' size='sm' onClick={() => onToggleActive(user.id)} className='min-h-[44px]'>
                {user.active ? 'Desativar' : 'Ativar'}
              </Button>
            </>
          ) : (
            <span className='text-text-muted text-[12px]'>—</span>
          )}
        </div>
      </td>
    </tr>
  )
}
